import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { cva } from 'class-variance-authority';
/**
 * Badge Component (Layer 1 Primitive)
 *
 * A small status indicator with color variants for different states.
 * Built on design tokens for consistent theming.
 *
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Failed</Badge>
 * <Badge variant="warning" size="lg">Pending</Badge>
 * ```
 */
const badgeVariants = cva(
// Base styles
'inline-flex items-center font-medium rounded-md transition-all duration-200 ease-out shadow-xs', {
    variants: {
        variant: {
            default: 'bg-uw-surface-elevated text-uw-text-primary border border-uw-border-default',
            primary: 'bg-uw-brand-primary text-uw-text-inverse',
            success: 'bg-uw-success text-uw-text-inverse',
            warning: 'bg-uw-warning text-uw-text-inverse',
            error: 'bg-uw-error text-uw-text-inverse',
            info: 'bg-uw-info text-uw-text-inverse',
            memory: 'bg-uw-primitive-memory-surface text-uw-primitive-memory border border-uw-primitive-memory/20',
            canon: 'bg-uw-primitive-canon-surface text-uw-primitive-canon border border-uw-primitive-canon/20',
            player: 'bg-uw-primitive-player-surface text-uw-primitive-player border border-uw-primitive-player/20',
            npc: 'bg-uw-primitive-npc-surface text-uw-primitive-npc border border-uw-primitive-npc/20',
            template: 'bg-uw-primitive-template-surface text-uw-primitive-template border border-uw-primitive-template/20',
            card: 'bg-uw-primitive-card-surface text-uw-primitive-card border border-uw-primitive-card/20',
            beat: 'bg-uw-primitive-beat-surface text-uw-primitive-beat border border-uw-primitive-beat/20',
            goal: 'bg-uw-primitive-goal-surface text-uw-primitive-goal border border-uw-primitive-goal/20',
            'category-data': 'bg-uw-category-data-surface text-uw-category-data border border-uw-category-data/20',
            'category-template': 'bg-uw-category-template-surface text-uw-category-template border border-uw-category-template/20',
            'category-gameplay': 'bg-uw-category-gameplay-surface text-uw-category-gameplay border border-uw-category-gameplay/20',
        },
        size: {
            sm: 'px-2 py-0.5 text-xs gap-1',
            md: 'px-2.5 py-1 text-sm gap-1.5',
            lg: 'px-3 py-1.5 text-base gap-2',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'md',
    },
});
export const Badge = React.forwardRef(({ children, variant = 'default', size = 'md', icon, removable = false, onRemove, className = '', ...props }, ref) => {
    return (_jsxs("span", { ref: ref, className: `${badgeVariants({ variant, size })} ${className}`, ...props, children: [icon && (_jsx("span", { className: "mr-1 flex items-center", "aria-hidden": "true", children: icon })), _jsx("span", { children: children }), (removable || onRemove) && (_jsx("button", { type: "button", onClick: (e) => {
                    e.stopPropagation();
                    onRemove?.();
                }, className: "ml-1 flex items-center hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-uw-interactive-primary focus:ring-offset-1 rounded-full", "aria-label": "Remove", children: _jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) }))] }));
});
Badge.displayName = 'Badge';
//# sourceMappingURL=Badge.js.map